import { useState, useCallback } from "react";
import { TaxEntity, TDSRateEntity } from "../../domain/entities/TaxEntity";
import { taxRepository } from "../../data/repositories/TaxRepositoryImpl";

export interface UseTaxesReturn {
  taxes: TaxEntity[];
  tdsRates: TDSRateEntity[];
  loading: boolean;
  error: string | null;
  fetchTaxSpecification: (specification: string) => Promise<void>;
  fetchTDSRates: () => Promise<void>;
  clearError: () => void;
}

export const useTaxes = (): UseTaxesReturn => {
  const [taxes, setTaxes] = useState<TaxEntity[]>([]);
  const [tdsRates, setTdsRates] = useState<TDSRateEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTaxSpecification = useCallback(
    async (specification: string): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const result = await taxRepository.fetchTaxSpecification(specification);
        if (result.success) {
          setTaxes(result.data.taxes);
        } else {
          setError("Failed to fetch tax specification");
        }
      } catch (err: any) {
        setError(err.message || "An error occurred while fetching taxes");
        console.error("Error fetching tax specification:", err);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const fetchTDSRates = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const result = await taxRepository.fetchTDSRates();
      if (result.success) {
        setTdsRates(result.data.tds_rates);
      } else {
        setError("Failed to fetch TDS rates");
      }
    } catch (err: any) {
      setError(err.message || "An error occurred while fetching TDS rates");
      console.error("Error fetching TDS rates:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    taxes,
    tdsRates,
    loading,
    error,
    fetchTaxSpecification,
    fetchTDSRates,
    clearError,
  };
};

